import { useState, useMemo, useCallback } from "react";

// Compares two values handling numbers, strings, dates and nulls.
// Nulls/undefined always go to the bottom regardless of direction.
const compare = (a, b) => {
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  const aNum = typeof a === "number" ? a : Number(a);
  const bNum = typeof b === "number" ? b : Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && (typeof a === "number" || typeof b === "number" || /^-?\d+(\.\d+)?$/.test(String(a)))) {
    return aNum - bNum;
  }

  return String(a).localeCompare(String(b), "es", { sensitivity: "base", numeric: true });
};

const readInitial = (storageKey, fallback) => {
  if (!storageKey) return fallback;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "key" in parsed && "direction" in parsed) {
      return parsed;
    }
  } catch (e) { /* ignore */ }
  return fallback;
};

// Sorts an array of objects by a configurable column.
// - getValue: (item, key) => value to compare. Defaults to item[key].
// - storageKey: if provided, persists { key, direction } in localStorage.
// - initial: { key, direction } default before any user click.
export const useSortableData = (data, { getValue, storageKey, initial } = {}) => {
  const [sortConfig, setSortConfig] = useState(() =>
    readInitial(storageKey, initial || { key: null, direction: "asc" })
  );

  const persist = useCallback((next) => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
  }, [storageKey]);

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => {
      const next = prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" };
      persist(next);
      return next;
    });
  }, [persist]);

  const getSortIndicator = useCallback((key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  }, [sortConfig]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const accessor = getValue || ((item, key) => item?.[key]);
    const copy = [...data];
    copy.sort((a, b) => {
      const cmp = compare(accessor(a, sortConfig.key), accessor(b, sortConfig.key));
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [data, sortConfig, getValue]);

  return { sortedData, sortConfig, requestSort, getSortIndicator };
};
